import React, { useCallback, useState } from "react";
import { Alert } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { ReactComponent as WhatsappIcon } from "./assets/whatsapp.svg";
import { ReactComponent as Whatsapp2Icon } from "./assets/whatsapp-2.svg";
import { CRMAccountCard } from "../CRMAccountCard";
import { CRMAccountTile } from "../CRMAccountTile";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { ReactComponent as RemoveIcon } from "./assets/remove.svg";
import { ReactComponent as PenIcon } from "./assets/pen.svg";
import { ReactComponent as ShuffleIcon } from "./assets/shuffle.svg";
import { ReactComponent as PhoneIcon } from "./assets/phone.svg";
import { ReactComponent as FileIcon } from "./assets/file.svg";
import { ReactComponent as EditIcon } from "./assets/edit.svg";
import { ReactComponent as CpuIcon } from "./assets/cpu.svg";
import { useWhatsappAccount } from "../../../../providers/WhatsappAccountProvider/useWhatsappAccount";
import { useManageAccountsDrawer } from "../../providers/ManageAccountsDrawerContext";

export const WhatsappAccountCard: React.FC<{
  hideReconnectOption?: boolean;
  isClickable?: boolean;
  variant?: "card" | "tile";
}> = ({ hideReconnectOption, isClickable, variant = "card" }) => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { navigateToPage } = useNavigation();
  const {
    whatsappAgent,
    reconnectWhatsappAccount,
    isConnected,
    connectWhatsappAccount,
  } = useWhatsappAccount();
  const { openManageAccountDrawer, isInboxModule } = useManageAccountsDrawer();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();

  const [disconnectWhatsappAgent, { isLoading }] =
    integrationsApi.useDisconnectWhatsappAgentAccountMutation();

  const [isDisconnectAlertOpen, setIsDisconnectAlertOpen] = useState(false);

  const accountName = whatsappAgent?.name || "";

  const handleRemoveAccount = useCallback(async () => {
    const resp = await disconnectWhatsappAgent({
      entityId,
      meta: {
        successMessage: t({
          id: "client-support-page.whatsapp-was-disconnected",
          defaultMessage: "WhatsApp was disconnected",
        }),
      },
    });

    if (resp && "data" in resp) {
      openManageAccountDrawer(undefined);
      if (isInboxModule) {
        navigateToPage("clientSupport");
      }
    }
  }, [
    disconnectWhatsappAgent,
    entityId,
    isInboxModule,
    navigateToPage,
    openManageAccountDrawer,
    t,
  ]);

  const disconnectAlert = (
    <Alert
      isOpen={isDisconnectAlertOpen}
      onRequestClose={() => setIsDisconnectAlertOpen(false)}
      title={t({
        id: "client-support-page.disconnect-account",
        defaultMessage: "Disconnect Account",
      })}
      content={t(
        {
          id: "client-support-page.disconnect-whatsapp-account",
          defaultMessage:
            "Are you sure you want to disconnect {account} WhatsApp account?",
        },
        {
          account: (
            <span className="font-medium text-primary">{accountName}</span>
          ),
        }
      )}
      buttons={[
        {
          text: t({
            id: "common.cancel",
            defaultMessage: "Cancel",
          }),
          color: "grey",
          role: "close",
        },
        {
          text: t({
            id: "client-support-page.disconnect",
            defaultMessage: "Disconnect",
          }),
          onClick: async (_, { closeAlert }) => {
            closeAlert();
            await handleRemoveAccount();
          },
        },
      ]}
    />
  );

  if (variant === "tile") {
    const hasAccount = !!whatsappAgent;

    const handleClick = () => {
      if (!isClickable) {
        return;
      }

      if (hasAccount) {
        transitionTo({ name: "whatsappAccount" });
        return;
      }

      connectWhatsappAccount().then(() => openManageAccountDrawer(undefined));
    };

    return (
      <CRMAccountTile
        isClickable={isClickable}
        isConnected={hasAccount}
        title={t({
          id: "client-support-page.whatsapp",
          defaultMessage: "WhatsApp",
        })}
        onClick={handleClick}
        icon={<Whatsapp2Icon />}
        features={
          hasAccount
            ? [
                { icon: <EditIcon />, label: accountName },
                {
                  icon: <PhoneIcon />,
                  label: whatsappAgent?.phone_number.display_phone_number ?? "",
                },
              ]
            : [
                {
                  icon: <CpuIcon />,
                  label: t({
                    id: "client-support-page.whatsapp-tile-feature-official-api",
                    defaultMessage: "Official WhatsApp API",
                  }),
                },
                {
                  icon: <FileIcon />,
                  label: t({
                    id: "client-support-page.whatsapp-tile-feature-templates",
                    defaultMessage: "Manage templates",
                  }),
                },
                {
                  icon: <ShuffleIcon />,
                  label: t({
                    id: "client-support-page.whatsapp-tile-feature-automated-workflows",
                    defaultMessage: "Automated workflows",
                  }),
                },
                {
                  icon: <PenIcon />,
                  label: t({
                    id: "client-support-page.whatsapp-tile-feature-customize-appearance",
                    defaultMessage: "Customize appearance",
                  }),
                },
              ]
        }
        status={
          hasAccount ? (isConnected ? "connected" : "disconnected") : undefined
        }
      />
    );
  }

  if (!whatsappAgent) return null;
  return (
    <>
      <CRMAccountCard
        img={whatsappAgent.profile[0]?.profile_picture_url}
        title={whatsappAgent.name}
        buttonProps={
          !hideReconnectOption && !isConnected
            ? {
                text: t({
                  id: "client-support-page.reconnect",
                  defaultMessage: "Reconnect",
                }),
                onClick: () =>
                  reconnectWhatsappAccount().then(() =>
                    openManageAccountDrawer(undefined)
                  ),
              }
            : undefined
        }
        onClick={
          isClickable
            ? () => transitionTo({ name: "whatsappAccount" })
            : undefined
        }
        icon={WhatsappIcon}
        description={
          isConnected
            ? `Account ID: ${whatsappAgent.waba_id}  •  ${whatsappAgent.phone_number.display_phone_number}`
            : t({
                id: "client-support-page.whatsapp-account-disconnected",
                defaultMessage:
                  "WhatsApp Account is disconnected. Please reconnect to continue using WhatsApp CRM",
              })
        }
        status={isConnected ? "connected" : "disconnected"}
        menuItems={[
          [
            {
              id: "disconnect-account",
              label: t({
                id: "client-support-page.disconnect-account",
                defaultMessage: "Disconnect Account",
              }),
              icon: <RemoveIcon />,
              onSelect: () => setIsDisconnectAlertOpen(true),
              isDisabled: isLoading,
            },
          ],
        ]}
      />
      {disconnectAlert}
    </>
  );
};
